import {
  AfterViewInit,
  Directive,
  ElementRef,
  EmbeddedViewRef, inject,
  Input,
  OnDestroy, Renderer2,
  TemplateRef
} from '@angular/core';
import { createPopper, Placement } from '@popperjs/core';
import { Instance } from '@popperjs/core/lib/types';
import { debounceTime, fromEvent, merge, Subscription, tap } from 'rxjs';
import { DOCUMENT } from '@angular/common';
import { DeviceUtils } from '../../core/utils/DeviceUtils';

@Directive({
  selector: '[appTooltip]',
  standalone: true
})
export class TooltipDirective implements OnDestroy, AfterViewInit {

  @Input() mode: 'dark' | 'light' = 'dark';
  @Input() placement: Placement = 'top';
  @Input() maxWidth = '384px';
  @Input() content = '';
  @Input() delay = 0;
  @Input() shouldHideOnMouseMove = false;
  @Input() trigger: 'click' | 'hover' = 'hover';
  @Input() wrapHostForHover = false;
  @Input() keepOpenedOnClick = false;
  @Input() set hide(hide: boolean) {
    this._hide = hide;

    if (this._hide) {
      this.destroyTooltip();
    }
  };
  @Input() templateRef: TemplateRef<any>;
  @Input() set templateRefContext(templateRefContext: any) {
    this._templateRefContext = templateRefContext;

    this.buildTooltipContent();
  };

  public isVisible = false;

  private _mouseLeaved = false;
  private _showSuppressed = false;
  private _hide = false;
  private _isClicked = false;
  private _tooltip: HTMLDivElement;
  private _popperInstance: Instance;
  private _templateRefContext: any;
  private _timeout: ReturnType<typeof setTimeout>;
  private _mutationObserver: MutationObserver;
  private _hoverHostWrapper: HTMLElement | null = null;
  private readonly _subs$ = new Subscription();
  private readonly _elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly _renderer = inject(Renderer2);
  private readonly _document = inject(DOCUMENT);

  public ngAfterViewInit(): void {
    if (!DeviceUtils.isTouchDevice) {
      if (this.wrapHostForHover) {
        this.ensureHoverHostWrapper();
      }

      const hoverSurface = this.getHoverSurface();

      if (this.trigger === 'hover') {
        this._subs$.add(
          fromEvent(hoverSurface, 'mouseenter').pipe(
            tap(() => this._mouseLeaved = false),
            debounceTime(this.delay)
          ).subscribe(this.onMouseEnter.bind(this))
        );
        this._subs$.add(
          fromEvent(hoverSurface, 'mouseleave').pipe(
            tap(() => {
              if (!this._isClicked) {
                this._mouseLeaved = true;
                this._showSuppressed = false;
              }
            }),
          ).subscribe(this.onMouseLeave.bind(this))
        );

        this.initPointerInteractionGuards(hoverSurface);

        if (this.keepOpenedOnClick) {
          this.initClickEvents();
        }
      } else if (this.trigger === 'click') {
        this.initClickEvents();
      }

      if (this.shouldHideOnMouseMove) {
        this._subs$.add(
          fromEvent<MouseEvent>(hoverSurface, 'mousemove').pipe(
            debounceTime(50),
          ).subscribe(this.onMouseMove.bind(this))
        );
      }

      this.initMutationObserver();
    }
  }

  private getHoverSurface(): HTMLElement {
    return this._hoverHostWrapper ?? this._elementRef.nativeElement;
  }

  private ensureHoverHostWrapper(): void {
    if (this._hoverHostWrapper || DeviceUtils.isTouchDevice) {
      return;
    }

    const host = this._elementRef.nativeElement;
    const parent = host.parentNode;

    if (!parent) {
      return;
    }

    const wrapper = this._renderer.createElement('span') as HTMLElement;
    wrapper.style.display = 'inherit';

    if (host.classList && host.classList.length > 0) {
      host.classList.forEach((cls: string) => {
        if (cls === 'disabled' || cls.includes('button')) {
          return;
        }

        this._renderer.addClass(wrapper, cls);
      });
    }

    this._renderer.insertBefore(parent as HTMLElement, wrapper, host);
    this._renderer.appendChild(wrapper, host);
    this._hoverHostWrapper = wrapper;
  }

  private removeHoverHostWrapper(): void {
    if (!this._hoverHostWrapper) {
      return;
    }

    const wrapper = this._hoverHostWrapper;
    const host = this._elementRef.nativeElement;
    const parent = wrapper.parentNode;
    if (parent) {
      this._renderer.insertBefore(parent as HTMLElement, host, wrapper);
      this._renderer.removeChild(parent as HTMLElement, wrapper);
    }
    this._hoverHostWrapper = null;
  }

  private onMouseEnter(): void {
    if (this._elementRef.nativeElement.isConnected && !this._mouseLeaved && !this._showSuppressed) {
      this.showTooltip();
    }
  }

  private onMouseDown(): void {
    this._showSuppressed = true;
    this.hideTooltip();
  }

  private initPointerInteractionGuards(hoverSurface: HTMLElement): void {
    this._subs$.add(
      fromEvent(hoverSurface, 'mousedown').subscribe(this.onMouseDown.bind(this))
    );

    this._subs$.add(
      fromEvent<PointerEvent>(this._document, 'pointerdown').subscribe((event) => {
        if (event.pointerType === 'touch') {
          this._showSuppressed = true;
          this.hideTooltip();
        }
      })
    );
  }

  private onMouseMove(): void {
    if (this._timeout) {
      clearTimeout(this._timeout);
    }

    if (this.isVisible) {
      this.hideTooltip();
    }

    this._timeout = setTimeout(() => {
      if (this._elementRef.nativeElement.isConnected && !this._mouseLeaved && !this.isVisible) {
        this.showTooltip();
      }
    }, 500);
  }

  private onMouseLeave(): void {
    if (!this._isClicked && this._elementRef.nativeElement.isConnected) {
      this.hideTooltip();
    }
  }

  private onClick(): void {
    if (this._elementRef.nativeElement.isConnected) {
      if (this.isVisible && this._isClicked) {
        this.hideTooltip();
        this._isClicked = false;
      } else {
        this.hideTooltip();
        this._isClicked = true;
        this.showTooltip();
      }
    }
  }

  private onWindowClick(event: MouseEvent): void {
    const surface = this.getHoverSurface();
    if (
      this.isVisible &&
      this._tooltip &&
      !this._tooltip.contains(event.target as Node) &&
      !surface.contains(event.target as Node)
    ) {
      this.hideTooltip();
      this._isClicked = false;
    }
  }

  private destroyTooltip(): void {
    this._tooltip?.remove();
    this._popperInstance?.destroy();
  }

  private showTooltip(): void {
    if (!this._hide && (this.content || this.templateRef)) {
      this._tooltip = this._renderer.createElement('div') as HTMLDivElement;
      this._tooltip.classList.add('tooltip', this.mode);
      this._tooltip.style.maxWidth = this.maxWidth;

      this._renderer.appendChild(this._document.body, this._tooltip);
      this.buildTooltipContent();
      this.isVisible = true;
    }
  }

  private hideTooltip(): void {
    this.destroyTooltip();
    this.isVisible = false;
  }

  private createPopper(): void {
    this._popperInstance = createPopper(this.getHoverSurface(), this._tooltip, {
      placement: this.placement,
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, 12],
          },
        },
      ],
    });
  }

  private buildTooltipContent(): void {
    if (this._tooltip && this._elementRef.nativeElement.isConnected) {
      let embeddedView: EmbeddedViewRef<{ arg: any }>;

      if (this.templateRef) {
        embeddedView = this.templateRef.createEmbeddedView({ arg: this._templateRefContext });
        embeddedView.detectChanges();
      }

      this._tooltip.innerHTML = `
          <div class="tooltip__content">
            ${embeddedView ? embeddedView.rootNodes[0].outerHTML : this.content}
          </div>
          <div class="arrow" data-popper-arrow></div>
        `;

      if (this._popperInstance) {
        this._popperInstance.destroy();
      }

      this.createPopper();
    }
  }

  private initMutationObserver(): void {
    this._mutationObserver = new MutationObserver(mutations => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList' && !this._elementRef.nativeElement.isConnected) {
          this.hideTooltip();
        }
      }
    });

    this._mutationObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  private initClickEvents(): void {
    this._subs$.add(
      fromEvent<MouseEvent>(this._elementRef.nativeElement, 'click').subscribe(this.onClick.bind(this))
    );
    this._subs$.add(
      merge(
        fromEvent<MouseEvent>(window, 'click'),
        fromEvent<MouseEvent>(window, 'contextmenu')
      ).subscribe(this.onWindowClick.bind(this))
    );
  }

  public ngOnDestroy(): void {
    this.destroyTooltip();
    this._subs$.unsubscribe();
    this._mutationObserver?.disconnect();
    this.removeHoverHostWrapper();
  }
}
